import {react, useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import getTaskFlows from '../functions/API/getTaskFlows.js'
import postArchiveTaskFlow from '../functions/API/postArchiveTaskFlow.js'
import postUnarchiveTaskFlow from '../functions/API/postUnarchiveTaskFlow.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import NewTaskFlowForm from '../forms/newTaskFlow.js';
import SlideOverRight from '../components/slideOverRight.js';
import Loader from '../components/loader.js'
import DeleteModal from '../modals/delete.js'
import ConfirmationModal from '../modals/confirmation.js'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { ChevronDown, MoreVertical, Pencil, Archive, ArchiveRestore, Trash2, ListOrdered } from 'lucide-react'

export default function TaskFlows() {
    const navigate = useNavigate();
    const [newTaskFlowForm, setNewTaskFlowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedTaskFlow, setSelectedTaskFlow] = useState(null);
    const [taskflows, setTaskFlows] = useState();
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskFlowToDelete, setTaskFlowToDelete] = useState(null);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [filter, setFilter] = useState('active'); // 'all', 'active', 'archived'
    const [allTaskFlows, setAllTaskFlows] = useState([]);

    async function GetAPIData() {
        let TaskFlowsResponse = await getTaskFlows();

        // Check if response is OK and data exists
        if (TaskFlowsResponse.response.ok && TaskFlowsResponse.data && TaskFlowsResponse.data.task_flows && Array.isArray(TaskFlowsResponse.data.task_flows)) {
            setAllTaskFlows(TaskFlowsResponse.data.task_flows);
        } else {
            // Handle error or empty response
            if (!TaskFlowsResponse.response.ok) {
                toast.error('Failed to load task flows. Please try again.');
            }
            setAllTaskFlows([]);
        }

        setIsLoaded(true);
    }

    function getFilteredTaskFlows() {
        if (filter === 'all') return allTaskFlows;
        if (filter === 'active') return allTaskFlows.filter(tf => !tf.is_archived);
        if (filter === 'archived') return allTaskFlows.filter(tf => tf.is_archived);
        return allTaskFlows;
    }

    function getFilterLabel() {
        if (filter === 'all') return 'All Task Flows';
        if (filter === 'active') return 'Active';
        if (filter === 'archived') return 'Archived';
        return 'All Task Flows';
    }

    function setFormState(){
        setEditMode(false);
        setSelectedTaskFlow(null);
        setNewTaskFlowForm(true);
    }

    function handleEdit(taskFlow){
        setEditMode(true);
        setSelectedTaskFlow(taskFlow);
        setNewTaskFlowForm(true);
    }

    function handleDeleteClick(taskFlow){
        setTaskFlowToDelete(taskFlow);
        setDeleteModalOpen(true);
    }

    async function handleArchive(taskFlow){
        if (actionLoading[taskFlow.id]) return;

        setActionLoading({...actionLoading, [taskFlow.id]: true});

        const response = await postArchiveTaskFlow(taskFlow.id);

        if(response.response.ok){
            toast.success('Task flow archived successfully.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to archive task flow.');
        }

        setActionLoading({...actionLoading, [taskFlow.id]: false});
    }

    async function handleUnarchive(taskFlow){
        if (actionLoading[taskFlow.id]) return;

        setActionLoading({...actionLoading, [taskFlow.id]: true});

        const response = await postUnarchiveTaskFlow(taskFlow.id);

        if(response.response.ok){
            toast.success('Task flow unarchived successfully.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to unarchive task flow.');
        }

        setActionLoading({...actionLoading, [taskFlow.id]: false});
    }

    function refreshData(){
        setShouldRefresh(shouldRefresh + 1);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh]);

    const filteredTaskFlows = getFilteredTaskFlows();

    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div className="">
                        <div className="sm:flex sm:items-center">
                            <div className="sm:flex-auto">
                                <h1 className="text-2xl text-left leading-6 text-gray-900 dark:text-white col-span-3">Task Flows</h1>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                    Manage task flows for onboarding, requests, and administrative processes
                                </p>
                            </div>
                            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex items-center gap-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline">
                                            Filter: {getFilterLabel()}
                                            <ChevronDown className="ml-2 h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuItem onSelect={() => setFilter('all')}>
                                            All Task Flows
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => setFilter('active')}>
                                            Active
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => setFilter('archived')}>
                                            Archived
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Button onClick={setFormState}>
                                    Add task flow
                                </Button>
                            </div>
                        </div>

                        <div className="mt-4 flow-root">
                            <div className="overflow-x-auto">
                                <div className="inline-block min-w-full py-2 align-middle lg:pr-2">
                                    <table className="min-w-full divide-y divide-gray-300 dark:divide-zinc-700">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">
                                                    Name
                                                </th>
                                                <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                                    Description
                                                </th>
                                                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-zinc-700 bg-white dark:bg-transparent">
                                            {filteredTaskFlows.map((taskFlow) => {
                                                const isArchived = taskFlow.is_archived || false;
                                                const truncatedDescription = taskFlow.description && taskFlow.description.length > 100
                                                    ? taskFlow.description.substring(0, 100) + '...'
                                                    : taskFlow.description;

                                                return (
                                                    <tr key={taskFlow.id} onClick={() => navigate(`/taskflows/${taskFlow.id}`)} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
                                                        <td className="whitespace-nowrap py-3 text-sm">
                                                            <span className="font-medium text-gray-900 dark:text-white">
                                                                {taskFlow.name}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 text-sm text-gray-500">
                                                            {truncatedDescription}
                                                        </td>
                                                        <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-0" onClick={(e) => e.stopPropagation()}>
                                                            <div className="flex items-center justify-end gap-2">
                                                                {isArchived && (
                                                                    <Badge variant="destructive">Archived</Badge>
                                                                )}
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" size="icon">
                                                                            <MoreVertical className="h-4 w-4" />
                                                                            <span className="sr-only">Open menu</span>
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onSelect={() => navigate(`/taskflows/${taskFlow.id}`)}>
                                                                        <ListOrdered className="mr-2 h-4 w-4" />
                                                                        Manage Steps
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem onSelect={() => handleEdit(taskFlow)}>
                                                                        <Pencil className="mr-2 h-4 w-4" />
                                                                        Edit Flow
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onSelect={() => isArchived ? handleUnarchive(taskFlow) : handleArchive(taskFlow)}
                                                                        disabled={actionLoading[taskFlow.id]}
                                                                    >
                                                                        {isArchived ? (
                                                                            <>
                                                                                <ArchiveRestore className="mr-2 h-4 w-4" />
                                                                                Unarchive
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Archive className="mr-2 h-4 w-4" />
                                                                                Archive
                                                                            </>
                                                                        )}
                                                                    </DropdownMenuItem>
                                                                    {isArchived && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onSelect={() => handleDeleteClick(taskFlow)}
                                                                                className="text-red-600 focus:text-red-600"
                                                                            >
                                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                                Delete
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {filteredTaskFlows.length === 0 && (
                                        <div className="text-center py-8">
                                            <p className="text-sm text-gray-500">No task flows found.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <SlideOverRight visible={newTaskFlowForm} setVisible={setNewTaskFlowForm}>
                    <NewTaskFlowForm
                        setVisible={setNewTaskFlowForm}
                        refresh={setShouldRefresh}
                        refreshValue={shouldRefresh}
                        editMode={editMode}
                        existingData={selectedTaskFlow}
                    />
                </SlideOverRight>
                <DeleteModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    IDToDelete={taskFlowToDelete?.id}
                    refresh={refreshData}
                    deleteAction="deleteTaskFlow"
                    title="Delete Task Flow"
                    message={`Are you sure you want to delete "${taskFlowToDelete?.name}"? This action cannot be undone.`}
                />
            </AdminSidebar>
        </>
    );
}
